import { useState, useMemo, useEffect } from "react";
import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import { usePromotionSelection } from "../../../components/PromotionIQ/PromotionSelectionContext";
import { MetricCard } from "../../../components/PromotionIQ/MetricCard";
import { Slider } from "../../../ui/slider";
import { Switch } from "../../../ui/switch";
import { Checkbox } from "../../../ui/checkbox";
import {
  retailers,
  skus,
  toolCategories,
  calculateProjectedMetrics,
  formatCurrency,
  getRetailerById,
  plannedPromotions,
  historicalPromotions,
} from "../../../data/PromotionIQ/PromotionData";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import { Gauge, TrendingUp, AlertTriangle, Target } from "lucide-react";
import React from "react";

const DEFAULTS = {
  retailer: "R1",
  sku: "S1",
  discountPct: 15,
  discountCents: 30,
  duration: 14,
  tools: ["display", "paid_social"] as string[],
};

const resolvePromotion = (promotionId?: string) => {
  if (!promotionId) return null;
  const planned = plannedPromotions.find((p) => p.promotion_id === promotionId);
  if (planned) {
    return {
      retailer_id: planned.retailer_id,
      sku_id: planned.sku_id,
      discount_pct: planned.planned_discount_pct,
      discount_cents: planned.planned_discount_cents,
      tools: planned.tools,
    };
  }
  const historical = historicalPromotions.find((p) => p.promotion_id === promotionId);
  if (historical) {
    return {
      retailer_id: historical.retailer_id,
      sku_id: historical.sku_id,
      discount_pct: historical.discount_depth_pct,
      discount_cents: historical.discount_depth_cents,
      tools: historical.tools,
    };
  }
  return null;
};

const PromotionInvestmentBuilder = () => {
  const { selectedPromotionId } = usePromotionSelection();
  const sourcePromo = useMemo(
    () => resolvePromotion(selectedPromotionId ?? undefined),
    [selectedPromotionId]
  );

  const [selectedRetailer, setSelectedRetailer] = useState(sourcePromo?.retailer_id ?? DEFAULTS.retailer);
  const [selectedSku, setSelectedSku] = useState(sourcePromo?.sku_id ?? DEFAULTS.sku);
  const [discountMode, setDiscountMode] = useState<"percent" | "cents">("percent");
  const [discountPct, setDiscountPct] = useState(sourcePromo?.discount_pct ?? DEFAULTS.discountPct);
  const [discountCents, setDiscountCents] = useState(sourcePromo?.discount_cents ?? DEFAULTS.discountCents);
  const [duration, setDuration] = useState(DEFAULTS.duration);
  const [selectedTools, setSelectedTools] = useState<string[]>(sourcePromo?.tools ?? DEFAULTS.tools);

  // Re-sync form state when the user navigates between promotions without remount.
  useEffect(() => {
    if (sourcePromo) {
      setSelectedRetailer(sourcePromo.retailer_id);
      setSelectedSku(sourcePromo.sku_id);
      setDiscountPct(sourcePromo.discount_pct);
      setDiscountCents(sourcePromo.discount_cents);
      setSelectedTools(sourcePromo.tools);
    } else {
      setSelectedRetailer(DEFAULTS.retailer);
      setSelectedSku(DEFAULTS.sku);
      setDiscountPct(DEFAULTS.discountPct);
      setDiscountCents(DEFAULTS.discountCents);
      setSelectedTools(DEFAULTS.tools);
    }
  }, [sourcePromo]);

  const retailer = getRetailerById(selectedRetailer);
  const sku = skus.find(s => s.sku_id === selectedSku);
  const elasticity = retailer?.elasticity_sensitivity || "Medium";

  // Calculate base spend from discount and duration
  const baseSpend = useMemo(() => {
    const discountValue = discountMode === "percent" ? discountPct : (discountCents / 199) * 100;
    return 50000 + (discountValue * 2000) + (duration * 1000) + (selectedTools.length * 5000);
  }, [discountMode, discountPct, discountCents, duration, selectedTools]);

  const discountValue = discountMode === "percent" ? discountPct : (discountCents / 199) * 100;
  const metrics = calculateProjectedMetrics(baseSpend, discountValue, selectedTools, elasticity);

  const handleToolToggle = (toolId: string) => {
    setSelectedTools(prev => 
      prev.includes(toolId) ? prev.filter(t => t !== toolId) : [...prev, toolId]
    );
  };

  return (
    <div className="space-y-6">
      <ScreenHeader
        title={selectedPromotionId ? `Promotion Investment Builder · ${selectedPromotionId}` : "Promotion Investment Builder"}
        question={sourcePromo ? "Edit the investment for this promotion." : "Let's design the investment."}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column - Controls */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Setup */}
          <div className="border border-border bg-card p-6">
            <h3 className="section-header mb-4">Investment Setup</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-athena mb-2 block">Retailer</label>
                <select
                  value={selectedRetailer}
                  onChange={(e) => setSelectedRetailer(e.target.value)}
                  className="input-athena"
                >
                  {retailers.map((r) => (
                    <option key={r.retailer_id} value={r.retailer_id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-athena mb-2 block">Product Line</label>
                <select
                  value={selectedSku}
                  onChange={(e) => setSelectedSku(e.target.value)}
                  className="input-athena"
                >
                  {skus.map((s) => (
                    <option key={s.sku_id} value={s.sku_id}>{s.variety}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Price Depth */}
          <div className="border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="section-header">Price Depth</h3>
              <ToggleGroup type="single" value={discountMode} onValueChange={(v) => v && setDiscountMode(v as "percent" | "cents")}>
                <ToggleGroupItem value="percent" className="px-4">Percentage</ToggleGroupItem>
                <ToggleGroupItem value="cents" className="px-4">Cents Off</ToggleGroupItem>
              </ToggleGroup>
            </div>
            
            {discountMode === "percent" ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-muted-foreground">Discount Depth</span>
                  <span className="text-3xl font-bold text-primary">{discountPct}%</span>
                </div>
                <Slider
                  value={[discountPct]}
                  onValueChange={(v) => setDiscountPct(v[0])}
                  min={5}
                  max={35}
                  step={1}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>5%</span>
                  <span className="text-warning">18% threshold</span>
                  <span>35%</span>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-muted-foreground">Cents Off (from ${sku?.base_price})</span>
                  <span className="text-3xl font-bold text-primary">{discountCents}¢</span>
                </div>
                <Slider
                  value={[discountCents]}
                  onValueChange={(v) => setDiscountCents(v[0])}
                  min={10}
                  max={70}
                  step={5}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>10¢</span>
                  <span>70¢</span>
                </div>
              </div>
            )}
          </div>

          {/* Duration */}
          <div className="border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="section-header">Promotion Duration</h3>
              <span className="text-2xl font-bold text-primary">{duration} days</span>
            </div>
            <Slider
              value={[duration]}
              onValueChange={(v) => setDuration(v[0])}
              min={7}
              max={28}
              step={7}
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1 week</span>
              <span>2 weeks</span>
              <span>3 weeks</span>
              <span>4 weeks</span>
            </div>
          </div>

          {/* Tool Selection */}
          <div className="border border-border bg-card p-6">
            <h3 className="section-header mb-4">Tool Selection (Multi-Select)</h3>
            <div className="space-y-6">
              {toolCategories.map((category) => (
                <div key={category.category}>
                  <h4 className="text-sm font-medium text-primary mb-3">{category.category}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {category.tools.map((tool) => (
                      <label
                        key={tool.id}
                        className={`flex items-center gap-3 p-3 border cursor-pointer transition-all ${
                          selectedTools.includes(tool.id)
                            ? "border-primary bg-primary/10"
                            : "border-border bg-muted/20 hover:bg-muted/40"
                        }`}
                      >
                        <Checkbox
                          checked={selectedTools.includes(tool.id)}
                          onCheckedChange={() => handleToolToggle(tool.id)}
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{tool.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {tool.lift_contribution * 100}% lift • {tool.cost_multiplier}x cost
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Live Outputs */}
        <div className="space-y-6">
          <div className="border border-primary/50 bg-primary/5 p-6 sticky top-6">
            <h3 className="section-header mb-4">Live Investment Outputs</h3>
            
            <div className="space-y-4">
              <div className="p-4 bg-card border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="h-4 w-4 text-primary" />
                  <span className="text-sm text-muted-foreground">Incremental Volume</span>
                </div>
                <p className="text-3xl font-bold">{metrics.incrementalUnits.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">{sku?.unit_measure}</p>
              </div>

              <div className="p-4 bg-card border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4 text-success" />
                  <span className="text-sm text-muted-foreground">Net Margin</span>
                </div>
                <p className={`text-3xl font-bold ${metrics.projectedMargin >= 0 ? "text-success" : "text-destructive"}`}>
                  {formatCurrency(metrics.projectedMargin)}
                </p>
              </div>

              <div className="p-4 bg-card border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <Gauge className="h-4 w-4 text-primary" />
                  <span className="text-sm text-muted-foreground">ROI</span>
                </div>
                <p className="text-3xl font-bold text-primary">{metrics.roi}x</p>
              </div>

              <div className="p-4 bg-card border border-border">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className={`h-4 w-4 ${
                    metrics.riskScore === "Low" ? "text-success" : 
                    metrics.riskScore === "Medium" ? "text-warning" : "text-destructive"
                  }`} />
                  <span className="text-sm text-muted-foreground">Risk-Adjusted Return</span>
                </div>
                <p className={`text-xl font-bold ${
                  metrics.riskScore === "Low" ? "text-success" : 
                  metrics.riskScore === "Medium" ? "text-warning" : "text-destructive"
                }`}>
                  {metrics.riskScore} Risk
                </p>
              </div>

              {/* Confidence Meter */}
              <div className="p-4 bg-card border border-border">
                <span className="text-sm text-muted-foreground">Confidence Meter</span>
                <div className="mt-2 h-4 bg-muted">
                  <div
                    className={`h-4 ${
                      metrics.confidence >= 0.75 ? "bg-success" : 
                      metrics.confidence >= 0.6 ? "bg-warning" : "bg-destructive"
                    }`}
                    style={{ width: `${metrics.confidence * 100}%` }}
                  />
                </div>
                <p className="text-right text-sm font-bold mt-1">{Math.round(metrics.confidence * 100)}%</p>
              </div>

              {/* Trade Spend */}
              <div className="p-4 bg-muted/30 border border-border">
                <span className="text-sm text-muted-foreground">Estimated Trade Spend</span>
                <p className="text-2xl font-bold text-foreground">{formatCurrency(baseSpend)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Insight */}
      <div className="border border-primary/50 bg-primary/5 p-6">
        <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">Purpose</p>
        <p className="text-lg">
          This is where price + tools are treated as <span className="text-primary font-bold">one integrated decision</span>. 
          Trade-offs are made explicit before money is committed.
        </p>
      </div>
    </div>
  );
};

export default PromotionInvestmentBuilder;
